import { useState } from 'react';
import { useMobileAuth } from '../../context/MobileAuthContext';
import { AlertTriangle, LogOut, CreditCard, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const TIERS = [
  { value: 'basic', name: 'Basic', desc: '2 operators, 1 user included' },
  { value: 'advanced', name: 'Advanced', desc: '2 operators, 1 user included' },
  { value: 'professional', name: 'Professional', desc: '2 operators, 1 user included' },
];

export default function MobileSubscriptionRequired() {
  const { operator, api, logout, isWorker, subscriptionStatus } = useMobileAuth();
  const [loading, setLoading] = useState(null);
  const isCancelled = subscriptionStatus === 'cancelled';

  const handleSelectTier = async (tier) => {
    setLoading(tier);
    try {
      const res = await api.post('/api/billing/checkout/subscription', {
        tier,
        origin_url: window.location.origin,
      });
      if (res.data?.checkout_url) {
        window.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to start checkout');
    } finally {
      setLoading(null);
    }
  };

  const handleLogout = async () => {
    try { await api.post('/api/mobile/auth/logout'); } catch {}
    logout();
  };

  // Workers can't manage subscriptions — show info-only message
  if (isWorker && isWorker()) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[var(--m-bg)]" data-testid="mobile-suspended-worker">
        <AlertTriangle className="h-14 w-14 text-red-400 mb-4" />
        <h1 className="text-2xl font-bold text-[var(--m-text)] mb-2">
          {isCancelled ? 'Account Cancelled' : 'Account Suspended'}
        </h1>
        <p className="text-slate-400 text-center text-sm mb-6 max-w-xs leading-relaxed">
          {isCancelled
            ? 'Your company\'s account has been cancelled. Please contact your administrator for more information.'
            : 'Your company\'s account has been suspended. Please contact your administrator to reactivate.'}
        </p>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-slate-400 hover:text-[var(--m-text)] transition-colors text-sm"
          data-testid="mobile-suspended-logout"
        >
          <LogOut className="h-4 w-4" /> Log Out
        </button>
      </div>
    );
  }

  // Cancelled admin — no tier selection, just message + logout
  if (isCancelled) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-6 bg-[var(--m-bg)]" data-testid="mobile-cancelled-admin">
        <AlertTriangle className="h-14 w-14 text-red-500 mb-4" />
        <h1 className="text-2xl font-bold text-[var(--m-text)] mb-2">Account Cancelled</h1>
        <p className="text-slate-400 text-center text-sm mb-6 max-w-xs leading-relaxed">
          This account has been cancelled and is no longer active. Please contact support if you believe this is an error.
        </p>
        <button
          onClick={handleLogout}
          className="flex items-center gap-2 text-slate-400 hover:text-[var(--m-text)] transition-colors text-sm"
          data-testid="mobile-cancelled-logout"
        >
          <LogOut className="h-4 w-4" /> Log Out
        </button>
      </div>
    );
  }

  // Admin / dispatcher — show paywall with Stripe checkout
  return (
    <div className="min-h-screen flex flex-col items-center px-4 pt-12 pb-8 bg-[var(--m-bg)]" data-testid="mobile-suspended-admin">
      <AlertTriangle className="h-12 w-12 text-red-400 mb-3" />
      <h1 className="text-2xl font-bold text-[var(--m-text)] mb-1">Account Suspended</h1>
      <p className="text-slate-400 text-center text-sm mb-6 max-w-xs">
        Select a subscription plan to restore access for{' '}
        <span className="text-[#ffd108] font-medium">{operator?.company_name || 'your company'}</span>.
      </p>

      <div className="w-full max-w-sm space-y-3 mb-8">
        {TIERS.map((tier) => (
          <button
            key={tier.value}
            onClick={() => handleSelectTier(tier.value)}
            disabled={loading !== null}
            className={`w-full p-4 rounded-xl border text-left transition-all active:scale-[0.98] ${
              tier.value === 'advanced'
                ? 'border-[#ffd108]/60 bg-[#ffd108]/5'
                : 'border-slate-700 bg-slate-800/60'
            }`}
            data-testid={`mobile-select-tier-${tier.value}`}
          >
            <div className="flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-[var(--m-text)] font-semibold">{tier.name}</span>
                  {tier.value === 'advanced' && (
                    <span className="text-[10px] font-bold bg-[#ffd108] text-black px-1.5 py-0.5 rounded">POPULAR</span>
                  )}
                </div>
                <p className="text-slate-500 text-xs mt-0.5">{tier.desc}</p>
              </div>
              {loading === tier.value ? (
                <Loader2 className="h-5 w-5 text-[#ffd108] animate-spin" />
              ) : (
                <CreditCard className="h-5 w-5 text-slate-500" />
              )}
            </div>
          </button>
        ))}
      </div>

      <button
        onClick={handleLogout}
        className="flex items-center gap-2 text-slate-500 hover:text-[var(--m-text)] transition-colors text-sm"
        data-testid="mobile-suspended-logout"
      >
        <LogOut className="h-4 w-4" /> Log Out
      </button>
    </div>
  );
}
